import { Button } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

import { Add } from '../../../../../../design-system/Icons/Add';
import { Close } from '../../../../../../design-system/Icons/Close';
import { Info } from '../../../../../../design-system/Icons/Info';
import { CircularLoader } from '../../../../../../design-system/Loader/CircularLoader';
import { SidePanelViewMode } from '../../../../SidePanel/types';

import {
    deleteProofOfDocumentsRequest,
    postProofOfDocumentsRequest,
} from '../../../../../../resources/contract/actions';
import {
    areProofOfDocumentsLoading as areProofOfDocumentsLoadingSelector,
    getFailedProofOfDocuments,
    getInitialProofOfDocuments,
    getUploadedNotAttachedProofOfDocuments,
} from '../../../../../../resources/contract/selectors';
import { ContractId, ProofDocument } from '../../../../../../resources/contract/types';
import { useAppDispatch, useAppSelector } from '../../../../../../shared/redux';

import s from './UploadProofOfDocuments.css';

type Props = {
    contractId: ContractId;
    isMissingProofOfDocuments: boolean;
    viewMode?: SidePanelViewMode;
};

export const UploadProofOfDocuments = ({
    contractId,
    isMissingProofOfDocuments,
    viewMode,
}: Props): JSX.Element => {
    const dispatch = useAppDispatch();
    const initialProofOfDocuments = useAppSelector(getInitialProofOfDocuments);
    const uploadedNotAttachedProofOfDocuments = useAppSelector(
        getUploadedNotAttachedProofOfDocuments,
    );
    const areProofOfDocumentsLoading = useAppSelector(areProofOfDocumentsLoadingSelector);
    const failedProofOfDocuments = useAppSelector(getFailedProofOfDocuments);

    const onDrop = (acceptedFiles: File[]) => {
        acceptedFiles.map((file) => {
            dispatch(postProofOfDocumentsRequest(contractId, file));
        });
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/pdf': ['.pdf'],
        },
        useFsAccessApi: false,
        multiple: true,
        noDrag: true,
        onDrop,
    });

    const retryUploadProofOfDocuments = (file: File) => {
        dispatch(postProofOfDocumentsRequest(contractId, file));
    };

    const renderProofOfDocuments = (proofOfDocuments: ProofDocument[]) => {
        return (
            proofOfDocuments &&
            proofOfDocuments.map((document) => (
                <div className={s.proofOfDocumentFile} key={document.name}>
                    <li>
                        <a href={document.url} download>
                            {document.name}
                        </a>
                    </li>
                    <button
                        type="button"
                        className={s.deleteButton}
                        data-testid="delete-proof-of-document"
                        onClick={() => dispatch(deleteProofOfDocumentsRequest(document.name))}
                    >
                        <Close color="var(--black)" />
                    </button>
                </div>
            ))
        );
    };

    return (
        <div
            className={s.proofOfDocumentsContainer}
            {...(viewMode && { 'data-contract-view': viewMode })}
        >
            <h3>
                <FormattedMessage id="contract.pricing.proof_of_documents.title" />
            </h3>
            <div className={s.uploadProofOfDocuments}>
                <p>
                    <FormattedMessage id="contract.pricing.proof_of_documents.description" />
                </p>
                <div {...getRootProps()}>
                    <input
                        id="upload-proof-documents"
                        data-testid="upload-proof-documents"
                        {...getInputProps()}
                    />
                    {areProofOfDocumentsLoading ? (
                        <Button
                            priority="secondary"
                            data-testid="proof-of-documents-upload-button"
                            disabled
                        >
                            <CircularLoader className={s.loader} data-testid="circular-loader" />
                            <FormattedMessage id="contract.pricing.proof_of_documents.button" />
                        </Button>
                    ) : (
                        <Button
                            priority="secondary"
                            onClick={(e) => e.preventDefault()}
                            className={s.uploadProofOfDocumentsButton}
                            data-testid="proof-of-documents-upload-button"
                        >
                            <Add color="var(--ink)" />
                            <FormattedMessage id="contract.pricing.proof_of_documents.button" />
                        </Button>
                    )}
                </div>
            </div>
            {isMissingProofOfDocuments &&
            uploadedNotAttachedProofOfDocuments.length === 0 &&
            failedProofOfDocuments.length === 0 ? (
                <p className={cx('red', s.uploadFailedDocument)}>
                    <Info color="var(--red)" />
                    <FormattedMessage id="contract.pricing.errors.proof_of_documents" />
                </p>
            ) : null}
            <ul>
                {renderProofOfDocuments(initialProofOfDocuments)}
                {renderProofOfDocuments(uploadedNotAttachedProofOfDocuments)}
                {failedProofOfDocuments &&
                    failedProofOfDocuments.map((document) => (
                        <div
                            className={cx(s.proofOfDocumentFile, s.failedProofOfDocumentFile)}
                            key={document.name}
                        >
                            <div className={s.uploadFailedDocument}>
                                <Info color="var(--red)" />
                                <li>{document.name}</li>
                            </div>
                            <div className={s.uploadFailedButtons}>
                                <button
                                    type="button"
                                    className={cx(s.retryButton, 'badge')}
                                    onClick={() => retryUploadProofOfDocuments(document)}
                                >
                                    {/* @ts-ignore TODO : adds translation */}
                                    <FormattedMessage id="contract.pricing.proof_of_documents.retry" />
                                </button>
                                <button
                                    type="button"
                                    className={s.deleteButton}
                                    data-testid="delete-proof-of-document"
                                    onClick={() =>
                                        dispatch(deleteProofOfDocumentsRequest(document.name))
                                    }
                                >
                                    <Close color="var(--black)" />
                                </button>
                            </div>
                        </div>
                    ))}
            </ul>
        </div>
    );
};
